import { useCallback, useEffect, useState } from 'react';

import { FromDateParams, PaginationParams } from '@customTypes/Api';
import { isAuthenticated } from '@features/loginSlice';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

const PAGE_SIZE = 200;

interface useFetchAllProps<T> {
  queryKeyRoot: string;
  apiFn: (params: PaginationParams & FromDateParams) => Promise<T[]>;
  date?: string;
}

const useFetchAll = <T,>(props: useFetchAllProps<T>) => {
  const { queryKeyRoot, apiFn, date } = props;
  const [page, setPage] = useState(1);

  const loggedIn = useSelector(isAuthenticated);

  const { data, error, fetchNextPage, isFetching, refetch } = useInfiniteQuery<T[], AxiosError, T[], (string | undefined)[]>({
    queryKey: [queryKeyRoot, date],

    queryFn: ({ pageParam = 1 }) =>
      apiFn({
        offset: (pageParam - 1) * PAGE_SIZE,
        maxReturn: PAGE_SIZE,
        ...(date && { earliestUpdatedAt: date }),
      }),
    enabled: loggedIn,
  });

  const onChangePage = useCallback(() => {
    if (!data || isFetching || error) {
      return;
    }
    const lastPage = data.pages.at(-1);

    if (lastPage && lastPage?.length < PAGE_SIZE) {
      return;
    }

    fetchNextPage({ pageParam: page + 1 }).then(() => {
      setPage((prev) => prev + 1);
    });
  }, [page, isFetching, data, error, fetchNextPage]);

  useEffect(() => {
    onChangePage();
  }, [onChangePage]);

  useEffect(() => {
    setPage(1);
  }, [date]);

  const refetchAll = () => {
    setPage(1);
    refetch();
  };

  return { data: data?.pages.flatMap((p) => p), error, isFetching, refetchAll };
};

export default useFetchAll;
